import React, { useState, useEffect, useMemo } from "react";
import { View, StyleSheet, ScrollView, Image, TouchableOpacity, ActivityIndicator, Platform } from "react-native";
import CustomDropdown from "../../../common/general/CustomDropDown/CustomDropdown";
import { DatePickerInput } from "react-native-paper-dates";
import { useForm, Controller } from "react-hook-form";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import ObjectivesWithCriteriaList from "../common/ObjectivesWithCriteriaList";
import ObjectiveForm from "../common/ObjectiveForm";
import VideoPoolManager from "../../../../containers/video/VideoPoolManager";
import LoadingOverlay from "../../../common/status/LoadingOverlay/LoadingOverlay";
import { CheckCircle, CheckMark, Redo, SparkleBlue, SparkleThin } from "../../../svgs/common";
import { CloseIcon, LearningRightArrow, LearningSideArrow, PlusCircle, UploadIcon } from "../../../svgIcons";
import { TrashIcon } from "@phosphor-icons/react";
import * as Sentry from "@sentry/react-native";
import { MutationFunction } from "@apollo/client";
import {
  ActivityState,
  GenerateAiTutorFromTitleMutation,
  GenerateAiTutorFromTitleMutationVariables,
  GenerateEvalCriteriaMutation,
  GenerateEvalCriteriaMutationVariables,
  GenerateObjectiveImageMutationVariables,
  GenerateObjectiveImagePromptsMutation,
  GenerateObjectiveImagePromptsMutationVariables,
  GetVisualizationQuery,
  RegenerateObjectiveImageMutationVariables,
} from "@axio/graphql/generated/graphql";
import type { CompletedObjectiveImageJob } from "../../../../hooks/customCoursework/useInteractiveLearningData";
import CustomRichTextEditor from "../../../TextEditor/CustomRichTextEditor";
import ObjectiveImageCard from "../../ObjectiveImageCard/ObjectiveImageCard";
import {
  AIImageGeneratingPlaceholder,
  aiImageTileContainerStyle,
} from "../../../chatNewInterface/interfaceComponents/generatedImageCard/GeneratedImageCard";

const CONTEXT_CHAR_LIMIT = 300000;
const MAX_IMAGES_PER_OBJECTIVE = 20;

const getReviewedImageKey = (objectiveId: string, url: string) => `${objectiveId}:${url}`;

const formatWebDateValue = (value?: Date | string | null) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseWebDateValue = (rawDate: string): Date | undefined => {
  if (!rawDate) return undefined;
  // `new Date("YYYY-MM-DD")` is parsed as UTC midnight, which shifts the
  // calendar day in negative-offset timezones (e.g. UTC-5 → previous day).
  // Parse the components and construct a local-time Date instead.
  const [year, month, day] = rawDate.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const WEB_DATE_INPUT_STYLE = {
  width: "100%",
  minHeight: 44,
  border: "1px solid #D1D5DB",
  borderRadius: 10,
  backgroundColor: "#FFFFFF",
  color: "#111827",
  fontSize: 14,
  paddingLeft: 12,
  paddingRight: 12,
  boxSizing: "border-box",
};

type ObjectiveImage = {
  id?: string;
  url: string;
  approved: boolean;
  description: string;
  isTeacherUploaded: boolean;
};

// Shape of a persisted ObjectiveImage as it arrives from the server (getAITutor query).
// Used wherever we read imagePool data off an Objective.
type ObjectiveImagePayload = {
  id?: string;
  url: string;
  isTeacherUploaded?: boolean;
  description?: string | null;
};

type ObjectiveItem = {
  id: string;
  objective: string;
  sequence: number;
  evalCriteria: string[];
  imagePool?: Array<ObjectiveImagePayload | null>;
};

type ActivityObjectiveInput = {
  id?: string;
  objective: string;
  sequence?: number;
  evalCriteria?: string[];
  imagePool?: Array<ObjectiveImagePayload | null>;
};

type VideoPoolItem = {
  videoId: string;
  objectiveText?: string;
  objectiveId?: string;
  objectiveSequence?: number;
  isTeacherAdded?: boolean;
};

type VideoPoolManagerItem = {
  videoId: string;
  objectiveId: string;
  objectiveSequence?: number;
  usage?: "rag" | "display" | "both";
  isTeacherAdded?: boolean;
};

type ImagePoolItem = {
  id?: string;
  url: string;
  objectiveText?: string;
  objectiveSequence?: number;
  isTeacherUploaded?: boolean;
  description?: string | null;
};

export type ActivityDataInput = {
  id?: string;
  title?: string;
  description?: string;
  targetAudience?: string;
  context?: string;
  videoPool?: VideoPoolItem[];
  dueDate?: string | Date | null;
  activityState?: ActivityState;
  scheduledDate?: Date | string;
  visualizationId?: string | null;
  rawObjectives?: ActivityObjectiveInput[];
  objectives?: ActivityObjectiveInput[];
  imagePool?: ImagePoolItem[];
};

type GeneratedObjective = {
  objective: string;
  sequence?: number | null;
  evalCriteria?: string[] | null;
};

type GenerateAiTutorResult = {
  data?: {
    generateAiTutorFromTitle?: {
      success?: boolean;
      data: {
        description: string;
        objectives: GeneratedObjective[];
      };
    };
  };
};

type GenerateEvalResult = {
  data?: {
    generateEvalCriteria?: {
      success?: boolean;
      data?: GeneratedObjective[];
    };
  };
};

type GenerationState = {
  isGenerating: boolean;
  step: string | null;
  progress: number;
  isCancellable: boolean;
};

type VisualizationState = {
  code?: string | null;
  visualizationType?: string | null;
  id: string;
} | null;

const createId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const maybeFormatDueDate = (date: string | Date | null | undefined) =>
  typeof date === "string" && date.endsWith("Z") ? new Date(date) : (date ?? "");

type FormSchema = {
  activityName: string;
  description: string;
  targetAudience: string;
  context: string;
  dueDate: Date | string;
  videoPool: VideoPoolItem[];
  activityState: ActivityState | undefined;
  scheduledDate: Date | string | undefined;
};

type InteractiveLearningFormCoreProps = {
  activityData?: ActivityDataInput;
  onSave: (data: ActivityDataInput) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  imageGenerationSupportFlag?: boolean;
  isFromLTI?: boolean;
  generateAiTutorFromTitle: MutationFunction<
    GenerateAiTutorFromTitleMutation,
    GenerateAiTutorFromTitleMutationVariables
  >;
  generateEvalCriteria: MutationFunction<GenerateEvalCriteriaMutation, GenerateEvalCriteriaMutationVariables>;
  visualizationData?: GetVisualizationQuery;
  stopPolling: () => void;
  refetchVisualization: (args: { id: string }) => void;
  generateObjectiveImagePromptsMutation: MutationFunction<
    GenerateObjectiveImagePromptsMutation,
    GenerateObjectiveImagePromptsMutationVariables
  >;
  requestObjectiveImageGeneration: (
    variables: GenerateObjectiveImageMutationVariables
  ) => Promise<CompletedObjectiveImageJob | null>;
  requestObjectiveImageRegeneration: (
    variables: RegenerateObjectiveImageMutationVariables
  ) => Promise<CompletedObjectiveImageJob | null>;
  handleObjectiveImageUpload: () => Promise<any>;
  resolveUploadedFileUrl: (file: { id: string; filePath?: string | null }) => Promise<string | null>;
};

type CustomButtonCompatProps = Omit<React.ComponentProps<typeof CustomButton>, "leftIcon"> & {
  leftIcon?: React.ReactNode;
};

type ObjectivesWithCriteriaListProps = {
  objectivesWithCriteria: ObjectiveItem[];
  onRemoveObjectiveWithCriteria: (index: number) => void;
  onUpdateCriteria: (index: number, updatedCriteria: string[]) => void;
  onRegenerateCriteria: (index: number, objective: ObjectiveItem) => void;
  onReorderObjectives: (reorderedObjectives: ObjectiveItem[]) => void;
  onEditObjectiveWithCriteria: (index: number, updatedObjective: ObjectiveItem) => void;
  disabled: boolean;
  isGeneratingCriteria: boolean;
  isRegeneratingCriteriaForObjective: Record<number, boolean>;
};

const CustomButtonCompat = CustomButton as React.ComponentType<CustomButtonCompatProps>;
const ObjectivesWithCriteriaListCompat =
  ObjectivesWithCriteriaList as React.ComponentType<ObjectivesWithCriteriaListProps>;

const InteractiveLearningFormCore = ({
  activityData,
  onSave,
  onCancel,
  isSubmitting,
  imageGenerationSupportFlag = false,
  isFromLTI = false,
  generateAiTutorFromTitle,
  generateEvalCriteria,
  visualizationData,
  stopPolling,
  refetchVisualization,
  generateObjectiveImagePromptsMutation,
  requestObjectiveImageGeneration,
  requestObjectiveImageRegeneration,
  handleObjectiveImageUpload,
  resolveUploadedFileUrl,
}: InteractiveLearningFormCoreProps) => {
  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormSchema>({
    defaultValues: {
      activityName: activityData?.title || "",
      description: activityData?.description || "",
      targetAudience: activityData?.targetAudience || "",
      context: activityData?.context ?? "",
      videoPool: activityData?.videoPool || [],
      dueDate: maybeFormatDueDate(activityData?.dueDate) || "",
      activityState: activityData?.activityState || ActivityState.Draft,
      scheduledDate: activityData?.scheduledDate || undefined,
    },
  });

  const checkExistenceOfEvalCriteriaForObjective = (objective: { evalCriteria?: string[] }) =>
    (objective.evalCriteria ?? []).some((item) => Boolean(item && item.trim() !== ""));

  const normalizeObjective = (objective: ActivityObjectiveInput, idx: number): ObjectiveItem => ({
    id: objective.id ?? createId(),
    objective: objective.objective,
    sequence: objective.sequence ?? idx + 1,
    evalCriteria: objective.evalCriteria ?? [],
    imagePool: objective.imagePool,
  });

  // State for current step
  const [currentStep, setCurrentStep] = useState(1);
  const [objectives, setObjectives] = useState<ObjectiveItem[]>(() =>
    (activityData?.rawObjectives ?? activityData?.objectives ?? []).map((objective, idx) =>
      normalizeObjective(objective, idx)
    )
  );
  const [hasGeneratedCriteria, setHasGeneratedCriteria] = useState(
    (activityData?.rawObjectives ?? activityData?.objectives ?? []).some((objective) =>
      checkExistenceOfEvalCriteriaForObjective(objective)
    )
  );

  const videoPoolManagerObjectives = useMemo(
    () =>
      objectives.map((objective, index) => ({
        objective: typeof objective === "string" ? objective : objective.objective,
        sequence: index + 1,
      })),
    [objectives]
  );

  const [videoPool, setVideoPool] = useState<VideoPoolItem[]>(activityData?.videoPool || []);
  const [hasGeneratedDemo, setHasGeneratedDemo] = useState(false);
  const [includeDemo, _setIncludeDemo] = useState(true);
  const [visualization, setVisualization] = useState<VisualizationState>(null);
  const [visualizationId, setVisualizationId] = useState<string | null>(activityData?.visualizationId || null);
  const [generatingVisualization, setGeneratingVisualization] = useState(false);
  const [, setVisualizationError] = useState<string | null>(null);
  const [newObjective, setNewObjective] = useState("");
  const [newCriteria, setNewCriteria] = useState("");
  const [regeneratingCriteriaForObjective, setRegeneratingCriteriaForObjective] = useState<Record<number, boolean>>({});
  const [showAddObjectiveForm, setShowAddObjectiveForm] = useState(false);
  const [descriptionEdited, setDescriptionEdited] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [activelyEditing, setActivelyEditing] = useState(false);

  const watchActivityName = watch("activityName");
  const watchDescription = watch("description");
  const watchTargetAudience = watch("targetAudience");
  const watchDueDate = watch("dueDate");
  const watchActivityState = watch("activityState");

  // Generation states
  const [generationState, setGenerationState] = useState<GenerationState>({
    isGenerating: false,
    step: null,
    progress: 0,
    isCancellable: true,
  });

  const [evalGenerationState, setEvalGenerationState] = useState<GenerationState>({
    isGenerating: false,
    step: null,
    isCancellable: true,
    progress: 0,
  });

  // Handler for video pool updates
  const handleVideoPoolUpdate = (updatedVideoPool: VideoPoolManagerItem[]) => {
    const mappedVideoPool: VideoPoolItem[] = updatedVideoPool.map((video) => ({
      videoId: video.videoId,
      objectiveId: video.objectiveId,
      objectiveSequence: video.objectiveSequence,
      isTeacherAdded: video.isTeacherAdded,
    }));
    setVideoPool(mappedVideoPool);
    setValue("videoPool", mappedVideoPool);
  };

  type FormattedObjective = {
    objective: string;
    evalCriteria: string[];
    __typename?: string;
  };

  type WindowWithObjectives = Window & {
    __lastGeneratedObjectives?: FormattedObjective[];
  };

  // Effect for visualization data
  useEffect(() => {
    const visualizations = visualizationData?.getVisualization?.data ?? [];
    if (visualizations.length > 0) {
      const vis = visualizations[0];
      if (vis.status === "completed") {
        setGeneratingVisualization(false);
        stopPolling();
        setVisualizationId(vis.id);
        setVisualization({
          code: vis.code,
          visualizationType: vis.visualizationType,
          id: vis.id,
        });
        setHasGeneratedDemo(true);
      } else if (vis.status === "failed") {
        setGeneratingVisualization(false);
        stopPolling();
        setVisualizationError("Failed to generate visualization");
      } else {
        setGeneratingVisualization(true);
      }
    }
  }, [visualizationData, stopPolling]);

  useEffect(() => {
    if (visualizationId && !visualization) {
      refetchVisualization({ id: visualizationId });
    }
  }, [visualizationId, refetchVisualization]);

  // Effect for initial load and hiding refresh objectives button
  useEffect(() => {
    if (objectives.length > 0) {
      setIsInitialLoad(true);
      setDescriptionEdited(false);
    } else {
      setIsInitialLoad(false);
      setDescriptionEdited(false);
    }
  }, []);

  useEffect(() => {
    if (watchDescription && objectives.length > 0 && !generationState.isGenerating) {
      setIsInitialLoad(false);
      setDescriptionEdited(true);
    }
  }, [watchDescription]);

  // Handle regenerating evaluation criteria for a specific objective
  const handleRegenerateObjectiveCriteria = async (index: number, objective: ObjectiveItem) => {
    try {
      // Set regenerating state for this specific objective
      setRegeneratingCriteriaForObjective((prev) => ({
        ...prev,
        [index]: true,
      }));

      const result = await generateEvalCriteria({
        variables: {
          input: {
            title: watchActivityName,
            description: watchDescription,
            objectives: [
              {
                objective: objective.objective,
                sequence: index + 1,
              },
            ],
            targetAudience: watchTargetAudience,
          },
        },
      });

      if (result.data?.generateEvalCriteria?.success) {
        const newCriteria = result.data.generateEvalCriteria.data;
        if (newCriteria && newCriteria.length > 0) {
          const criteriaArray = (newCriteria[0]?.evalCriteria ?? []).filter((item: string | null | undefined) =>
            Boolean(item)
          ) as string[];

          // Update just this objective's criteria
          const updatedObjectives = [...objectives];
          updatedObjectives[index] = {
            ...updatedObjectives[index],
            evalCriteria: criteriaArray,
          };

          setObjectives(updatedObjectives);
          setHasGeneratedCriteria(true);
        }
      }
    } catch (error) {
      console.error("Error regenerating evaluation criteria:", error);
    } finally {
      // Clear regenerating state for this objective
      setRegeneratingCriteriaForObjective((prev) => ({
        ...prev,
        [index]: false,
      }));
    }
  };

  const handleRemoveObjective = (index: number) => {
    const resequencedObjectives = objectives
      .filter((_, idx) => idx !== index)
      .map((objective, idx) => ({ ...objective, sequence: idx + 1 }));
    setObjectives(resequencedObjectives);

    if (resequencedObjectives.length === 0) {
      setHasGeneratedCriteria(false);
    }
  };

  const handleEditObjective = (index: number, updatedObjective: ObjectiveItem) => {
    const newObjectives = [...objectives];
    newObjectives[index] = { ...updatedObjective, sequence: index + 1 };
    setObjectives(newObjectives);
  };

  const handleAutoGenerate = async () => {
    if (!watchActivityName) return;

    try {
      setDescriptionEdited(false);
      setGenerationState({
        isGenerating: true,
        step: "Generating activity content",
        progress: 1,
        isCancellable: true,
      });

      const result = await generateAiTutorFromTitle({
        variables: {
          title: watchActivityName,
          targetAudience: watchTargetAudience,
        },
      });

      if (result.data?.generateAiTutorFromTitle?.success) {
        const content = result.data.generateAiTutorFromTitle.data;

        // Update state with generated content
        setValue("description", content.description);

        const structuredObjectives = content.objectives.map((obj: GeneratedObjective, idx: number) => ({
          id: createId(),
          objective: obj.objective,
          sequence: idx + 1,
          evalCriteria: [],
        }));

        setObjectives(structuredObjectives);

        setGenerationState((prev) => ({
          ...prev,
          step: "Generating evaluation criteria",
          progress: 2,
          isCancellable: false,
        }));

        setEvalGenerationState({
          isGenerating: true,
          step: "Generating evaluation criteria",
          progress: 1,
          isCancellable: false,
        });

        try {
          const evalResult = await generateEvalCriteria({
            variables: {
              input: {
                title: watchActivityName,
                description: content.description,
                objectives: content.objectives.map((obj: GeneratedObjective, idx: number) => ({
                  objective: obj.objective,
                  sequence: idx + 1,
                })),
                targetAudience: watchTargetAudience,
              },
            },
          });

          if (evalResult.data?.generateEvalCriteria?.success) {
            const newObjectives = (evalResult.data.generateEvalCriteria.data ?? []) as GeneratedObjective[];

            const processedObjectives = (newObjectives ?? [])
              .filter((obj: GeneratedObjective | null | undefined) => Boolean(obj))
              .map((obj: GeneratedObjective, idx: number) => ({
                id: createId(),
                objective: obj.objective,
                evalCriteria: (obj.evalCriteria ?? []).filter((item: string | null | undefined) => Boolean(item)),
                sequence: idx + 1,
              }));

            setObjectives(processedObjectives);
            setHasGeneratedCriteria(true);

            try {
              (window as WindowWithObjectives).__lastGeneratedObjectives = processedObjectives.map(
                (obj: ObjectiveItem) => ({
                  objective: obj.objective,
                  evalCriteria: [...obj.evalCriteria],
                  __typename: "FormattedObjective",
                })
              );
            } catch (e) {
              // Ignore if window assignment fails
            }
          }
        } catch (evalError) {
          console.error("Error generating evaluation criteria:", evalError);
        } finally {
          // Clear evaluation generation state
          setEvalGenerationState({
            isGenerating: false,
            step: null,
            isCancellable: true,
            progress: 0,
          });
        }
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setGenerationState({
        isGenerating: false,
        step: null,
        progress: 0,
        isCancellable: true,
      });
    }
  };

  const handleGenerateObjectivesAndCriteria = async () => {
    if (!watchActivityName) return;

    try {
      // Store the current description
      const currentDescription = watch("description");

      setDescriptionEdited(false);
      setGenerationState({
        isGenerating: true,
        step: "Generating objectives",
        progress: 1,
        isCancellable: true,
      });

      const result = await generateAiTutorFromTitle({
        variables: {
          title: watchActivityName,
          targetAudience: watchTargetAudience,
        },
      });

      if (result.data?.generateAiTutorFromTitle?.success) {
        const content = result.data.generateAiTutorFromTitle.data;

        const structuredObjectives = content.objectives.map((obj: GeneratedObjective, idx: number) => ({
          id: createId(),
          objective: obj.objective,
          sequence: idx + 1,
          evalCriteria: [],
        }));

        setObjectives(structuredObjectives);

        setGenerationState((prev) => ({
          ...prev,
          step: "Generating evaluation criteria",
          progress: 2,
          isCancellable: false,
        }));

        // Now set the evaluation generation state to true so loading indicators appear
        setEvalGenerationState({
          isGenerating: true,
          step: "Generating evaluation criteria",
          progress: 1,
          isCancellable: false,
        });

        // Generate evaluation criteria with new objectives but keep original description
        try {
          const evalResult = await generateEvalCriteria({
            variables: {
              input: {
                title: watchActivityName,
                description: currentDescription, // Using original description here
                objectives: content.objectives.map((obj: GeneratedObjective, idx: number) => ({
                  objective: obj.objective,
                  sequence: idx + 1,
                })),
                targetAudience: watchTargetAudience,
              },
            },
          });

          if (evalResult.data?.generateEvalCriteria?.success) {
            const newObjectives = (evalResult.data.generateEvalCriteria.data ?? []) as GeneratedObjective[];

            // Process the criteria
            const processedObjectives = (newObjectives ?? []).map((obj: GeneratedObjective, idx: number) => {
              const criteria = (obj.evalCriteria ?? []).filter(Boolean);
              const criteriaArray =
                criteria.length > 0 ? [criteria[0]] : ["Student demonstrates understanding of the objective"];

              return {
                id: createId(),
                objective: obj.objective,
                evalCriteria: criteriaArray,
                sequence: idx + 1,
              };
            });

            setObjectives(processedObjectives);
            setHasGeneratedCriteria(true);

            // Create global version for cloning
            try {
              (window as WindowWithObjectives).__lastGeneratedObjectives = processedObjectives.map(
                (obj: ObjectiveItem) => ({
                  objective: obj.objective,
                  evalCriteria: [...obj.evalCriteria],
                  __typename: "FormattedObjective",
                })
              );
            } catch (e) {
              // Ignore if window assignment fails
            }
          }
        } catch (evalError) {
          console.error("Error generating evaluation criteria:", evalError);
        } finally {
          // Clear evaluation generation state
          setEvalGenerationState({
            isGenerating: false,
            step: null,
            isCancellable: true,
            progress: 0,
          });
        }
      }
    } catch (error) {
      console.error("Error auto-generating objectives and criteria:", error);
    } finally {
      setGenerationState({
        isGenerating: false,
        step: null,
        progress: 0,
        isCancellable: true,
      });
    }
  };

  const onSubmit = (data: FormSchema) => {
    if (!data.activityName.trim()) {
      return;
    }

    setActivelyEditing(false);

    const formattedObjectives = objectives.map((objective, index) => ({
      objective: objective.objective,
      sequence: index + 1,
      evalCriteria: [...objective.evalCriteria],
    }));

    if (formattedObjectives.length === 0) {
      console.error("Cannot create AI Tutor without objectives");
      return;
    }

    // Create the activity data object with all required fields
    const activityData = {
      title: data.activityName,
      description: data.description,
      objectives: formattedObjectives,
      videoUrl: "",
      context: data.context,
      type: "Interactive Learning Experience",
      visualizationId: includeDemo ? visualizationId : null,
      targetAudience: data.targetAudience,
      dueDate: data.dueDate,
      __generatedObjectives: (() => {
        try {
          return (window as WindowWithObjectives).__lastGeneratedObjectives || null;
        } catch (e) {
          return null;
        }
      })(),
      videoPool: videoPool.map((video) => ({
        videoId: video.videoId,
        objectiveText: video.objectiveId || video.objectiveText,
        objectiveSequence: video.objectiveSequence,
        isTeacherAdded: video.isTeacherAdded || false,
      })),
      imagePool: objectives.flatMap((obj, idx) => {
        const seq = idx + 1;
        const objectiveText = obj.objective;
        const objectiveId = obj.id;
        const images = (objectiveImages[objectiveId] || []).slice(0, MAX_IMAGES_PER_OBJECTIVE);
        return images
          .filter((image) => image.approved)
          .map((image) => ({
            id: image.id,
            url: image.url,
            objectiveText,
            objectiveSequence: seq,
            isTeacherUploaded: image.isTeacherUploaded,
            description: image.description.trim() || null,
          }));
      }),
      activityState: data.activityState,
      scheduledDate: data.scheduledDate,
    };

    onSave(activityData);
  };

  // Handle step navigation
  const goToPreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const goToNextStep = () => {
    if (currentStep === 3) {
      setValue("videoPool", videoPool);
    }
    setCurrentStep(currentStep + 1);
  };

  // Render step indicator
  const renderStepIndicator = () => {
    return (
      <View style={styles.stepIndicator}>
        <CustomText text={`Step ${currentStep} of 5`} size="s" useTranslationText={false} />
        <View style={styles.stepProgressBar}>
          {[1, 2, 3, 4, 5].map((step) => (
            <View
              key={step}
              style={[
                styles.stepProgressSegment,
                step <= currentStep ? styles.stepProgressActive : styles.stepProgressInactive,
              ]}
            />
          ))}
        </View>
      </View>
    );
  };

  // Render navigation buttons
  const renderNavButtons = () => {
    const isLastStep = currentStep === 5;
    const isFirstStep = currentStep === 1;

    // Check if any evaluation criteria are empty after generation
    const hasEmptyEvalCriteria = objectives.some(
      (objective) =>
        !objective.evalCriteria ||
        objective.evalCriteria.length === 0 ||
        objective.evalCriteria.some((item) => !item || item.trim() === "")
    );

    // Require valid evaluation criteria if objectives exist
    const requiresEvalCriteria =
      currentStep === 1 && objectives.length > 0 && (!hasGeneratedCriteria || hasEmptyEvalCriteria);

    const isNextDisabled =
      (currentStep === 1 && (!watchActivityName || !watchDescription || objectives.length === 0)) ||
      requiresEvalCriteria;

    return (
      <View style={styles.navButtons}>
        {isFirstStep ? (
          <CustomButtonCompat
            text="Cancel"
            onPress={onCancel}
            styleType="primaryLight"
            size="s"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            style={styles.navButton}
          />
        ) : (
          <CustomButtonCompat
            text="Back"
            onPress={goToPreviousStep}
            styleType="primaryLight"
            size="s"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            style={styles.navButton}
          />
        )}

        <CustomSpacing type="horizontal" size="s" />

        {!isLastStep ? (
          <CustomButtonCompat
            text="Next"
            onPress={goToNextStep}
            styleType={isNextDisabled ? "disabled" : "primary"}
            size="s"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            style={[styles.navButton, isNextDisabled ? { backgroundColor: "#ccc" } : {}]}
            disabled={isNextDisabled}
          />
        ) : (
          <CustomButtonCompat
            text={activityData?.id ? "Update Activity" : "Create Activity"}
            onPress={handleSubmit(onSubmit)}
            styleType={isSubmitting ? "disabled" : "primary"}
            size="s"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            style={[styles.navButton, isSubmitting ? { backgroundColor: "#ccc" } : {}]}
            disabled={isSubmitting}
          />
        )}
      </View>
    );
  };

  // Render step 1 content - Basic information
  const renderStep1Content = () => {
    return (
      <View>
        <CustomText text="Let's Start with the Basics" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Provide key details about this activity. This will help us generate personalized content for your students."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="l" />
        <CustomText text="Activity name" size="m" weight="ultraBold" useTranslationText={false} />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          key={`activityName-input-${currentStep}`}
          name="activityName"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, onBlur, value } }) => {
            return (
              <CustomTextInput
                placeholder="e.g., Introduction to Velocity"
                onBlur={onBlur}
                onChangeText={(text: string) => {
                  onChange(text);
                }}
                value={value}
                editable={!generationState.isGenerating}
                style={styles.inputWithButtonField}
              />
            );
          }}
        />
        {errors.activityName && <CustomText text="Activity name is required" style={styles.errorText} />}

        <CustomSpacing type="vertical" size="l" />
        <CustomText text="Target Audience" size="m" weight="ultraBold" useTranslationText={false} />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          name="targetAudience"
          control={control}
          render={({ field: { onChange, value } }) => (
            <CustomDropdown
              placeholder="Select a target audience"
              options={[
                { label: "Elementary School", value: "Elementary School" },
                { label: "Middle School", value: "Middle School" },
                { label: "High School", value: "High School" },
                { label: "Undergraduate", value: "Undergraduate" },
                { label: "Graduate", value: "Graduate" },
              ]}
              value={value}
              onChange={onChange}
              dropdownStyle={styles.dropdown}
            />
          )}
        />
        <CustomSpacing type="vertical" size="s" />
        <CustomButtonCompat
          text="Generate Activity Content"
          onPress={handleAutoGenerate}
          loading={generationState.isGenerating}
          disabled={!watchActivityName || generationState.isGenerating}
          styleType="primaryTransparentStroke"
          size="s"
          leftIcon={<SparkleBlue width={16} height={16} />}
          textStyle={{ color: "#3E68FE" }}
        />

        <CustomSpacing type="vertical" size="l" />
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <CustomText text="Activity Description" size="m" weight="ultraBold" useTranslationText={false} />
        </View>
        <CustomSpacing type="vertical" size="m" />
        <View style={styles.editorContainer}>
          <Controller
            name="description"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => {
              const handleDescriptionChange = (content: string) => {
                setActivelyEditing(true);
                onChange(content);
                setValue("description", content, { shouldDirty: true, shouldValidate: false });
                if (objectives.length > 0 && !generationState.isGenerating) {
                  setDescriptionEdited(true);
                }
              };
              return (
                <CustomRichTextEditor
                  value={value}
                  onChange={handleDescriptionChange}
                  isSubmitting={generationState.isGenerating}
                  placeholder="Briefly describe what students will learn in this activity."
                  activelyEditing={activelyEditing}
                />
              );
            }}
          />
        </View>
        {errors.description && <CustomText text="Description is required" style={styles.errorText} />}

        <CustomSpacing type="vertical" size="xl" />

        {!isInitialLoad && descriptionEdited && (
          <>
            <CustomButtonCompat
              text="Refresh Objectives"
              onPress={handleGenerateObjectivesAndCriteria}
              loading={generationState.isGenerating}
              disabled={!watchActivityName || generationState.isGenerating}
              styleType="primaryTransparentStroke"
              size="s"
              leftIcon={<SparkleBlue width={16} height={16} />}
              textStyle={{ color: "#3E68FE", fontSize: 14 }}
            />
            <CustomSpacing type="vertical" size="m" />
          </>
        )}
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <CustomText
            text="Learning Objectives and Evaluation Criteria"
            size="m"
            weight="ultraBold"
            useTranslationText={false}
          />
        </View>
        <CustomText
          text="Add Learning Objectives with their Evaluation Criteria to help students understand what they will learn and how they will be evaluated."
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="m" />

        {/* Display existing objectives with criteria using the component */}
        {objectives.length > 0 && (
          <ObjectivesWithCriteriaListCompat
            objectivesWithCriteria={objectives}
            onRemoveObjectiveWithCriteria={handleRemoveObjective}
            onUpdateCriteria={(index: number, updatedCriteria: string[]) => {
              const newObjectives = [...objectives];
              if (newObjectives[index]) {
                newObjectives[index].evalCriteria = updatedCriteria;
                setObjectives(newObjectives);
              }
            }}
            onRegenerateCriteria={handleRegenerateObjectiveCriteria}
            onReorderObjectives={(reorderedObjectives: ObjectiveItem[]) => {
              setObjectives(reorderedObjectives);
            }}
            onEditObjectiveWithCriteria={handleEditObjective}
            disabled={generationState.isGenerating}
            isGeneratingCriteria={evalGenerationState.isGenerating || generationState.isGenerating}
            isRegeneratingCriteriaForObjective={regeneratingCriteriaForObjective}
          />
        )}

        <CustomSpacing type="vertical" size="m" />

        {/* Add Objective Button */}
        {!showAddObjectiveForm ? (
          <CustomButtonCompat
            text="Add Learning Objective"
            onPress={() => setShowAddObjectiveForm(true)}
            styleType="primaryTransparentStroke"
            size="s"
            disabled={generationState.isGenerating}
            leftIcon={<PlusCircle width={16} height={16} />}
            style={{ maxWidth: 220 }}
          />
        ) : (
          <ObjectiveForm
            objective={newObjective}
            criteria={newCriteria}
            onObjectiveChange={setNewObjective}
            onCriteriaChange={setNewCriteria}
            onSave={(data: { objective: string; criteria?: string }) => {
              const newObj = {
                id: createId(),
                objective: data.objective,
                sequence: objectives.length + 1,
                evalCriteria: data.criteria ? [data.criteria] : [],
              };
              setObjectives([...objectives, newObj]);
              setHasGeneratedCriteria(true);
              setNewObjective("");
              setNewCriteria("");
              setShowAddObjectiveForm(false);
            }}
            onCancel={() => setShowAddObjectiveForm(false)}
            disabled={generationState.isGenerating}
            isEditMode={false}
            saveButtonText="Add Objective with Criteria"
          />
        )}
        <CustomSpacing type="vertical" size="m" />
      </View>
    );
  };

  // Render step 2 content - Contextual Alignment
  const renderStep2Content = () => {
    return (
      <View>
        <CustomText
          text="Now let's add contextual alignment (optional)"
          size="l"
          weight="ultraBold"
          useTranslationText={false}
        />
        <CustomText
          text="Add additional context and enrich the AI's teaching focus for this activity."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="l" />
        <CustomText text="Contextual Alignment" size="m" weight="ultraBold" useTranslationText={false} />

        <CustomSpacing type="vertical" size="s" />
        <View style={styles.infoBox}>
          <CustomText text="What is Contextual Alignment?" size="s" weight="bold" useTranslationText={false} />
          <CustomText
            text="Contextual Alignment allows you to provide specific materials that will guide the AI in creating a personalized learning experience. You can add:"
            size="s"
            useTranslationText={false}
          />
          <View style={styles.bulletList}>
            <CustomText text="• Excerpts from textbooks or academic papers" size="s" useTranslationText={false} />
            <CustomText text="• Specific topics you want the AI to cover" size="s" useTranslationText={false} />
            <CustomText
              text="• Methodologies or teaching approaches to emphasize"
              size="s"
              useTranslationText={false}
            />
            <CustomText text="• Class-specific terminology or concepts" size="s" useTranslationText={false} />
          </View>
          <CustomText
            text="The AI will use this additional context to tailor its teaching approach to align with your specific educational needs and materials."
            size="s"
            useTranslationText={false}
          />
        </View>

        <CustomSpacing type="vertical" size="l" />
        <Controller
          key={`context-input-${currentStep}`}
          name="context"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <CustomTextInput
              placeholder="Add further Contextual Alignment (e.g., paste relevant textbook sections, list specific topics to cover, or describe preferred teaching approaches)"
              onBlur={onBlur}
              onChangeText={onChange}
              value={value}
              numberOfLines={4}
              multiline
              style={styles.multilineInput}
              maxLength={CONTEXT_CHAR_LIMIT}
            />
          )}
        />
        <View style={styles.infoRow}>
          <CustomText
            text={`${watch("context")?.length || 0} / ${CONTEXT_CHAR_LIMIT}`}
            size="xs"
            style={styles.counter}
            useTranslationText={false}
          />
        </View>
      </View>
    );
  };

  // Render step 3 content - Video Recommendations
  const renderStep3Content = () => {
    return (
      <View>
        <CustomText
          text="Add videos to your objectives (optional)"
          size="l"
          weight="ultraBold"
          useTranslationText={false}
        />
        <CustomText
          text="Use the tool below to generate video recommendations based on your learning objectives."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="m" />

        {objectives.length > 0 ? (
          <VideoPoolManager
            title={watchActivityName}
            description={watchDescription}
            objectives={videoPoolManagerObjectives}
            onVideoPoolUpdate={handleVideoPoolUpdate}
            initialVideoPool={videoPool as unknown as VideoPoolManagerItem[]}
          />
        ) : (
          <CustomText
            text="You need to add learning objectives in Step 1 before you can add videos."
            size="m"
            useTranslationText={false}
            style={styles.warningText}
          />
        )}
      </View>
    );
  };

  // Render step 4 content - Interactive Demo
  // Image generation state (Step 4)
  const [objectiveImages, setObjectiveImages] = useState<Record<string, ObjectiveImage[]>>({});
  const [isGeneratingImages, setIsGeneratingImages] = useState<Record<string, boolean>>({});
  const [expectedGenerateCount, setExpectedGenerateCount] = useState<Record<string, number>>({});
  const [generationBaselineCount, setGenerationBaselineCount] = useState<Record<string, number>>({});
  const [isAddingImage, setIsAddingImage] = useState<Record<string, boolean>>({});

  const appendGeneratedImage = (objectiveId: string, generated: CompletedObjectiveImageJob) => {
    if (typeof generated.url !== "string") return;
    setObjectiveImages((prev) => ({
      ...prev,
      [objectiveId]: [
        ...(prev[objectiveId] || []),
        {
          id: generated.id ?? undefined,
          url: generated.url,
          approved: false,
          description: generated.description ?? "",
          isTeacherUploaded: false,
        },
      ].slice(0, MAX_IMAGES_PER_OBJECTIVE),
    }));
  };

  const clearGenerationTracking = (objectiveId: string) => {
    setIsGeneratingImages((prev) => ({ ...prev, [objectiveId]: false }));
    setExpectedGenerateCount((prev) => {
      const next = { ...prev };
      delete next[objectiveId];
      return next;
    });
    setGenerationBaselineCount((prev) => {
      const next = { ...prev };
      delete next[objectiveId];
      return next;
    });
  };

  const [imageReviewState, setImageReviewState] = useState<{ objectiveId: string; index: number } | null>(null);
  const [regenerateFeedbackByImage, setRegenerateFeedbackByImage] = useState<Record<string, string>>({});
  const [regeneratingReviewedImageKeys, setRegeneratingReviewedImageKeys] = useState<Record<string, boolean>>({});
  const [reviewedImageAspectRatio, setReviewedImageAspectRatio] = useState<number | null>(null);

  const reviewedImageUrl = imageReviewState
    ? (objectiveImages[imageReviewState.objectiveId]?.[imageReviewState.index]?.url ?? null)
    : null;

  useEffect(() => {
    if (!reviewedImageUrl) {
      setReviewedImageAspectRatio(null);
      return;
    }

    Image.getSize(
      reviewedImageUrl,
      (width, height) => {
        if (width > 0 && height > 0) setReviewedImageAspectRatio(width / height);
      },
      () => setReviewedImageAspectRatio(null)
    );
  }, [reviewedImageUrl]);

  const getObjectiveIdentity = (obj: Pick<ObjectiveItem, "id" | "objective">, index: number) => ({
    id: obj.id,
    text: obj.objective,
    sequence: index + 1,
  });

  useEffect(() => {
    const existingObjectives = activityData?.rawObjectives ?? activityData?.objectives ?? [];
    const savedImagePool = activityData?.imagePool ?? [];
    const hasAnySource = existingObjectives.length > 0 || savedImagePool.length > 0;
    if (!hasAnySource) return;

    setObjectiveImages((prev) => {
      if (Object.keys(prev).length > 0) return prev;

      const objectiveTextToId = new Map<string, string>();
      const objectiveSeqToId = new Map<number, string>();
      objectives.forEach((obj, idx) => {
        const { id, text, sequence } = getObjectiveIdentity(obj, idx);
        objectiveSeqToId.set(sequence, id);
        if (text && !objectiveTextToId.has(text)) objectiveTextToId.set(text, id);
      });

      const next: Record<string, ObjectiveImage[]> = {};

      savedImagePool.forEach((img) => {
        if (!img?.url) return;
        const seqFromPayload = img.objectiveSequence;
        const idFromSeq = seqFromPayload ? objectiveSeqToId.get(seqFromPayload) : undefined;
        const idFromText = img.objectiveText ? objectiveTextToId.get(img.objectiveText) : undefined;
        const objectiveId = idFromSeq || idFromText;
        if (!objectiveId) return;
        if (!next[objectiveId]) next[objectiveId] = [];
        next[objectiveId].push({
          id: img?.id ?? undefined,
          url: img.url,
          approved: true,
          description: img.description ?? "",
          isTeacherUploaded: Boolean(img?.isTeacherUploaded),
        });
      });

      existingObjectives.forEach((objective) => {
        const seq = objective.sequence;
        const legacyPool: Array<ObjectiveImagePayload | null> =
          (objective as { imagePool?: Array<ObjectiveImagePayload | null> | null })?.imagePool || [];
        if (!seq || legacyPool.length === 0) return;
        const objectiveId = objectiveSeqToId.get(seq);
        if (!objectiveId) return;
        if (!next[objectiveId]) next[objectiveId] = [];
        legacyPool
          .filter((img) => Boolean(img?.url))
          .forEach((img) => {
            if (!img?.url) return;
            next[objectiveId].push({
              id: img?.id ?? undefined,
              url: img.url,
              approved: true,
              description: img.description ?? "",
              isTeacherUploaded: Boolean(img?.isTeacherUploaded),
            });
          });
      });

      Object.entries(next).forEach(([seq, images]) => {
        next[seq] = images.slice(0, MAX_IMAGES_PER_OBJECTIVE);
      });
      return Object.keys(next).length > 0 ? next : prev;
    });
  }, [activityData?.imagePool, activityData?.rawObjectives, activityData?.objectives, objectives]);

  const handleGenerateImagesForObjective = async (objectiveId: string, objectiveText: string, seq: number) => {
    if (!watchActivityName || !objectiveText) return;

    const existingCount = objectiveImages[objectiveId]?.length ?? 0;
    const remaining = Math.max(0, MAX_IMAGES_PER_OBJECTIVE - existingCount);
    if (remaining === 0) return;

    setIsGeneratingImages((prev) => ({ ...prev, [objectiveId]: true }));
    setGenerationBaselineCount((prev) => ({ ...prev, [objectiveId]: existingCount }));
    try {
      const promptsResponse = await generateObjectiveImagePromptsMutation({
        variables: {
          objectiveText,
          objectiveSequence: seq,
          activityTitle: watchActivityName,
          targetAudience: watchTargetAudience || null,
          activityDescription: watchDescription || null,
        },
      });
      const prompts = promptsResponse.data?.generateObjectiveImagePrompts?.data || [];
      if (prompts.length === 0) return;

      const generateCount = Math.min(prompts.length, remaining);
      setExpectedGenerateCount((prev) => ({ ...prev, [objectiveId]: generateCount }));

      await Promise.all(
        Array.from({ length: generateCount }, (_, i) =>
          requestObjectiveImageGeneration({
            objectiveText,
            objectiveSequence: seq,
            activityTitle: watchActivityName,
            prompt: prompts[i] ?? null,
          }).then((generated) => {
            if (generated) appendGeneratedImage(objectiveId, generated);
          })
        )
      );
    } finally {
      clearGenerationTracking(objectiveId);
    }
  };

  const handleGenerateOneForObjective = async (objectiveId: string, objectiveText: string, seq: number) => {
    if (!watchActivityName || !objectiveText) return;
    if (isGeneratingImages[objectiveId] || isAddingImage[objectiveId]) return;

    const remaining = Math.max(0, MAX_IMAGES_PER_OBJECTIVE - (objectiveImages[objectiveId]?.length ?? 0));
    if (remaining === 0) return;

    setIsAddingImage((prev) => ({ ...prev, [objectiveId]: true }));
    try {
      const promptsResponse = await generateObjectiveImagePromptsMutation({
        variables: {
          objectiveText,
          objectiveSequence: seq,
          activityTitle: watchActivityName,
          targetAudience: watchTargetAudience || null,
          activityDescription: watchDescription || null,
        },
      });
      const prompt = promptsResponse.data?.generateObjectiveImagePrompts?.data?.[0] ?? null;

      const generated = await requestObjectiveImageGeneration({
        objectiveText,
        objectiveSequence: seq,
        activityTitle: watchActivityName,
        prompt,
      });
      if (!generated?.url) return;
      const generatedUrl = generated.url;

      setObjectiveImages((prev) => {
        const current = prev[objectiveId] || [];
        return {
          ...prev,
          [objectiveId]: [
            ...current,
            {
              id: generated.id ?? undefined,
              url: generatedUrl,
              approved: false,
              description: generated.description ?? "",
              isTeacherUploaded: false,
            },
          ].slice(0, MAX_IMAGES_PER_OBJECTIVE),
        };
      });
    } finally {
      setIsAddingImage((prev) => ({ ...prev, [objectiveId]: false }));
    }
  };

  const handleRegenerateAllForObjective = async (objectiveId: string, objectiveText: string, seq: number) => {
    if (!objectiveText || !watchActivityName) return;
    if (isGeneratingImages[objectiveId]) return;

    const previousImages = (objectiveImages[objectiveId] || []).slice(0, MAX_IMAGES_PER_OBJECTIVE);

    setIsGeneratingImages((prev) => ({ ...prev, [objectiveId]: true }));
    setGenerationBaselineCount((prev) => ({ ...prev, [objectiveId]: 0 }));
    setObjectiveImages((prev) => ({ ...prev, [objectiveId]: [] }));

    try {
      const promptsResponse = await generateObjectiveImagePromptsMutation({
        variables: {
          objectiveText,
          objectiveSequence: seq,
          activityTitle: watchActivityName,
          targetAudience: watchTargetAudience || null,
          activityDescription: watchDescription || null,
        },
      });
      const prompts = promptsResponse.data?.generateObjectiveImagePrompts?.data || [];
      if (prompts.length === 0) {
        setObjectiveImages((prev) => ({ ...prev, [objectiveId]: previousImages }));
        return;
      }

      const generateCount = Math.min(prompts.length, MAX_IMAGES_PER_OBJECTIVE);
      setExpectedGenerateCount((prev) => ({ ...prev, [objectiveId]: generateCount }));

      await Promise.all(
        Array.from({ length: generateCount }, (_, i) =>
          requestObjectiveImageGeneration({
            objectiveText,
            objectiveSequence: seq,
            activityTitle: watchActivityName,
            prompt: prompts[i] ?? null,
          }).then((generated) => {
            if (generated) appendGeneratedImage(objectiveId, generated);
          })
        )
      );
    } catch {
      setObjectiveImages((prev) => ({ ...prev, [objectiveId]: previousImages }));
    } finally {
      clearGenerationTracking(objectiveId);
    }
  };

  const handleRemoveImage = (objectiveId: string, url: string) => {
    setObjectiveImages((prev) => ({
      ...prev,
      [objectiveId]: (prev[objectiveId] || []).filter((img) => img.url !== url),
    }));
  };

  const handleRegenerateSingle = async (objectiveId: string, objectiveText: string, url: string, feedback: string) => {
    if (!objectiveText || !feedback) return;

    const image = (objectiveImages[objectiveId] || []).find((img) => img.url === url);
    if (!image?.url) return;

    const regenerated = await requestObjectiveImageRegeneration({
      imageId: url,
      feedback,
      objectiveText,
    });
    if (!regenerated?.url) return;
    const regeneratedUrl = regenerated.url;

    setObjectiveImages((prev) => ({
      ...prev,
      [objectiveId]: (prev[objectiveId] || []).map((img) =>
        img.url === url
          ? {
              id: regenerated.id ?? undefined,
              url: regeneratedUrl,
              approved: false,
              description: regenerated.description ?? "",
              isTeacherUploaded: img.isTeacherUploaded,
            }
          : img
      ),
    }));
  };

  const handleUploadImageForObjective = async (objectiveId: string) => {
    const existingCount = objectiveImages[objectiveId]?.length ?? 0;
    const remaining = Math.max(0, MAX_IMAGES_PER_OBJECTIVE - existingCount);
    if (remaining === 0) return;

    const processed = await handleObjectiveImageUpload();
    if (!processed || processed.length === 0) return;

    const uploaded = processed[0];
    const url = await resolveUploadedFileUrl(uploaded.file);
    if (!url) return;

    setObjectiveImages((prev) => ({
      ...prev,
      [objectiveId]: [
        ...(prev[objectiveId] || []),
        {
          id: uploaded.file.id,
          url,
          approved: false,
          description: "",
          isTeacherUploaded: true,
        },
      ].slice(0, MAX_IMAGES_PER_OBJECTIVE),
    }));
  };

  const handleToggleApproveImage = (objectiveId: string, url: string) => {
    setObjectiveImages((prev) => ({
      ...prev,
      [objectiveId]: (prev[objectiveId] || []).map((img) =>
        img.url === url ? { ...img, approved: !img.approved } : img
      ),
    }));
  };

  const handleApproveAllForObjective = (objectiveId: string) => {
    setObjectiveImages((prev) => ({
      ...prev,
      [objectiveId]: (prev[objectiveId] || []).map((img) => ({ ...img, approved: true })),
    }));
  };

  const handleUpdateImageDescription = (objectiveId: string, url: string, description: string) => {
    setObjectiveImages((prev) => ({
      ...prev,
      [objectiveId]: (prev[objectiveId] || []).map((img) => (img.url === url ? { ...img, description } : img)),
    }));
  };

  const renderStep4Content = () => {
    const objectiveMetas = objectives.map((obj, idx) => getObjectiveIdentity(obj, idx));
    const reviewedObjectiveId = imageReviewState?.objectiveId ?? null;
    const reviewedIndex = imageReviewState?.index ?? 0;
    const reviewedMeta = reviewedObjectiveId
      ? (objectiveMetas.find((meta) => meta.id === reviewedObjectiveId) ?? null)
      : null;
    const reviewedImages = reviewedObjectiveId
      ? (objectiveImages[reviewedObjectiveId] || []).slice(0, MAX_IMAGES_PER_OBJECTIVE)
      : [];
    const reviewedImage = reviewedObjectiveId ? (reviewedImages[reviewedIndex] ?? null) : null;
    const reviewedImageKey =
      reviewedObjectiveId && reviewedImage ? getReviewedImageKey(reviewedObjectiveId, reviewedImage.url) : null;
    const reviewedRegenerateFeedback = reviewedImageKey ? (regenerateFeedbackByImage[reviewedImageKey] ?? "") : "";
    const isRegeneratingCurrentReviewedImage = reviewedImageKey
      ? !!regeneratingReviewedImageKeys[reviewedImageKey]
      : false;
    return (
      <View>
        <CustomText
          text={
            imageGenerationSupportFlag
              ? "Generate images for your objectives (optional)"
              : "Upload images for your objectives"
          }
          size="l"
          weight="ultraBold"
          useTranslationText={false}
        />
        <CustomText
          text={
            imageGenerationSupportFlag
              ? "Generate AI images for each learning objective. Review, approve, or regenerate images with feedback."
              : "Upload images for each learning objective. Review and approve your selections."
          }
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="m" />

        {objectiveMetas.map(({ id: objectiveId, text: title, sequence: seq }) => {
          const images = (objectiveImages[objectiveId] || []).slice(0, MAX_IMAGES_PER_OBJECTIVE);
          const generating = !!isGeneratingImages[objectiveId];
          const addingOne = !!isAddingImage[objectiveId];
          const isBusy = generating || addingOne;
          const canGenerateMore = images.length < MAX_IMAGES_PER_OBJECTIVE;
          const approvedCount = images.filter((img) => img.approved).length;
          const baselineCount = generationBaselineCount[objectiveId] ?? 0;
          const newImagesDuringRun = Math.max(0, images.length - baselineCount);
          const knownExpectedCount = expectedGenerateCount[objectiveId];
          const awaitingGenerateCount = generating && knownExpectedCount == null;
          const pendingPlaceholderCount =
            generating && knownExpectedCount != null ? Math.max(0, knownExpectedCount - newImagesDuringRun) : 0;
          const showEmptyOrLoadingState = (images.length === 0 && !generating) || awaitingGenerateCount;
          const showImageGrid = !awaitingGenerateCount && (generating || images.length > 0);

          return (
            <View key={`objective-images-${objectiveId}`} style={styles.objectiveSection}>
              <View style={styles.objectiveTitleBar}>
                <CustomText
                  text={title}
                  size="m"
                  weight="bold"
                  useTranslationText={false}
                  style={{ color: "#101828" }}
                />
              </View>

              <View style={styles.objectiveBody}>
                {showEmptyOrLoadingState && (
                  <View style={styles.emptyState}>
                    <View style={styles.emptyIconCircle}>
                      <SparkleThin width={20} height={20} />
                    </View>
                    <CustomSpacing type="vertical" size="s" />
                    <CustomText
                      text={awaitingGenerateCount ? "Generating images..." : "No images yet"}
                      size="m"
                      weight="bold"
                      useTranslationText={false}
                      style={{ color: "#344054" }}
                    />
                    <CustomSpacing type="vertical" size="xs" />
                    <CustomText
                      text={
                        awaitingGenerateCount
                          ? "Please wait while AI images are being prepared."
                          : imageGenerationSupportFlag
                            ? "Generate AI images or upload your own."
                            : "Upload your own images."
                      }
                      size="s"
                      useTranslationText={false}
                      style={{ color: "#667085" }}
                    />
                    <CustomSpacing type="vertical" size="m" />
                    <View style={styles.emptyActionsRow}>
                      {imageGenerationSupportFlag ? (
                        <CustomButtonCompat
                          text="Generate Images"
                          onPress={() => handleGenerateImagesForObjective(objectiveId, title, seq)}
                          disabled={isBusy || !canGenerateMore}
                          loading={awaitingGenerateCount}
                          styleType="primaryTransparentStroke"
                          size="s"
                          leftIcon={<SparkleBlue width={16} height={16} />}
                          textStyle={{ color: "#3E68FE" }}
                          style={{ justifyContent: "center", minWidth: 190 }}
                        />
                      ) : null}
                      <CustomButtonCompat
                        text="Upload"
                        onPress={() => handleUploadImageForObjective(objectiveId)}
                        disabled={isBusy}
                        styleType="primaryTransparentStroke"
                        size="s"
                        leftIcon={<UploadIcon width={16} height={16} color="#344054" />}
                        textStyle={{ color: "#344054" }}
                        style={{ justifyContent: "center", minWidth: 140 }}
                      />
                    </View>
                  </View>
                )}

                {showImageGrid && (
                  <>
                    {!generating && images.length > 0 ? (
                      <View style={styles.objectiveActionsRow}>
                        {imageGenerationSupportFlag ? (
                          <CustomButtonCompat
                            text="Regenerate All"
                            onPress={() => handleRegenerateAllForObjective(objectiveId, title, seq)}
                            disabled={isBusy}
                            styleType="primaryTransparentStroke"
                            size="s"
                            leftIcon={<Redo />}
                            textStyle={{ color: "#667085", fontWeight: 700 }}
                            style={styles.objectiveActionButton}
                          />
                        ) : null}
                        <CustomButtonCompat
                          text={`Approve All (${approvedCount}/${images.length})`}
                          onPress={() => handleApproveAllForObjective(objectiveId)}
                          styleType="primaryTransparentStroke"
                          size="s"
                          leftIcon={<CheckCircle width={18} height={18} />}
                          textStyle={{ color: "#209B51", fontWeight: 700 }}
                          style={[styles.objectiveActionButton, styles.approveAllButton]}
                        />
                      </View>
                    ) : null}

                    <View style={styles.grid}>
                      {images.map((img, i) => (
                        <View key={`image-${objectiveId}-${i}`} style={styles.gridItem}>
                          <ObjectiveImageCard
                            imageIndex={i}
                            thumbnailUrl={img.url}
                            isApproved={img.approved}
                            description={img.description}
                            onOpen={() => {
                              setImageReviewState({ objectiveId, index: i });
                            }}
                            onRemove={() => {
                              handleRemoveImage(objectiveId, img.url);
                              if (
                                imageReviewState &&
                                imageReviewState.objectiveId === objectiveId &&
                                imageReviewState.index === i
                              ) {
                                setImageReviewState(null);
                              }
                            }}
                            onToggleApprove={() => handleToggleApproveImage(objectiveId, img.url)}
                          />
                        </View>
                      ))}
                      {Array.from({ length: pendingPlaceholderCount }, (_, i) => (
                        <View key={`generating-${objectiveId}-${i}`} style={styles.gridItem}>
                          <AIImageGeneratingPlaceholder />
                        </View>
                      ))}
                      {addingOne && (
                        <View style={styles.gridItem}>
                          <AIImageGeneratingPlaceholder />
                        </View>
                      )}
                      {canGenerateMore && !generating && !addingOne && (
                        <View style={styles.gridItem}>
                          <View style={[aiImageTileContainerStyle, styles.generateTile]}>
                            <CustomText
                              text="+"
                              size="l"
                              weight="ultraBold"
                              useTranslationText={false}
                              style={{ color: "#98A2B3" }}
                            />
                            {imageGenerationSupportFlag ? (
                              <>
                                <CustomSpacing type="vertical" size="s" />
                                <TouchableOpacity
                                  style={styles.generateTileAction}
                                  onPress={() => handleGenerateOneForObjective(objectiveId, title, seq)}
                                  disabled={isBusy || !canGenerateMore}>
                                  <SparkleBlue width={16} height={16} />
                                  <CustomText
                                    text="Generate"
                                    size="s"
                                    weight="bold"
                                    useTranslationText={false}
                                    style={{ color: "#3E68FE" }}
                                  />
                                </TouchableOpacity>
                                <CustomSpacing type="vertical" size="s" />
                              </>
                            ) : null}
                            <TouchableOpacity
                              style={styles.generateTileAction}
                              onPress={() => handleUploadImageForObjective(objectiveId)}>
                              <UploadIcon width={16} height={16} color="#667085" />
                              <CustomText
                                text="Upload"
                                size="s"
                                weight="bold"
                                useTranslationText={false}
                                style={{ color: "#667085" }}
                              />
                            </TouchableOpacity>
                          </View>
                        </View>
                      )}
                    </View>
                  </>
                )}
              </View>
            </View>
          );
        })}

        <CustomSpacing type="vertical" size="s" />
        <View style={styles.infoBox}>
          <CustomText text="Only approved images are saved — any image you don't approve will be lost" size="xs" />
        </View>

        {reviewedImage && reviewedObjectiveId ? (
          <CustomModal
            visible={true}
            onRequestClose={() => setImageReviewState(null)}
            title={`Image ${reviewedIndex + 1} of ${reviewedImages.length}`}
            showCloseButton={true}
            exitFill="#667085"
            style={styles.imageReviewModal}
            bodyStyle={styles.imageReviewModalBody}>
            <ScrollView style={styles.imageReviewScroll} contentContainerStyle={styles.imageReviewScrollContent}>
              <View style={[styles.imageReviewImageWrap, { aspectRatio: reviewedImageAspectRatio ?? 1.8 }]}>
                <Image
                  source={{ uri: reviewedImage.url }}
                  style={styles.imageReviewImage}
                  resizeMode="contain"
                  accessibilityLabel={reviewedImage.description || "Image preview"}
                />
                {reviewedImages.length > 1 && reviewedIndex > 0 && (
                  <TouchableOpacity
                    style={[styles.imageNavButton, styles.imageNavLeft]}
                    onPress={() => setImageReviewState({ objectiveId: reviewedObjectiveId, index: reviewedIndex - 1 })}
                    accessibilityLabel="Previous image"
                    accessibilityRole="button"
                    accessibilityHint="Navigates to the previous image">
                    <LearningSideArrow />
                  </TouchableOpacity>
                )}
                {reviewedImages.length > 1 && reviewedIndex < reviewedImages.length - 1 && (
                  <TouchableOpacity
                    style={[styles.imageNavButton, styles.imageNavRight]}
                    onPress={() => setImageReviewState({ objectiveId: reviewedObjectiveId, index: reviewedIndex + 1 })}
                    accessibilityLabel="Next image"
                    accessibilityRole="button"
                    accessibilityHint="Navigates to the next image">
                    <LearningRightArrow />
                  </TouchableOpacity>
                )}
              </View>

              <CustomSpacing type="vertical" size="m" />
              <CustomText
                text="Description"
                size="s"
                weight="bold"
                useTranslationText={false}
                style={{ color: "#344054" }}
              />
              <CustomSpacing type="vertical" size="xs" />
              <CustomTextInput
                placeholder="Describe this image (e.g. a labeled diagram of the heart)"
                value={reviewedImage.description}
                onChangeText={(text: string) => {
                  if (!reviewedObjectiveId) return;
                  handleUpdateImageDescription(reviewedObjectiveId, reviewedImage.url, text);
                }}
                style={styles.imageReviewTextInput}
              />
              <CustomSpacing type="vertical" size="xs" />
              <CustomText
                text="Used by screen readers and as grounding for the AI teacher's references."
                size="xs"
                useTranslationText={false}
                style={{ color: "#98A2B3" }}
              />

              <CustomSpacing type="vertical" size="m" />
              {!reviewedImage.isTeacherUploaded && imageGenerationSupportFlag && (
                <>
                  <CustomText
                    text="Regenerate with feedback"
                    size="s"
                    weight="bold"
                    useTranslationText={false}
                    style={{ color: "#344054" }}
                  />
                  <CustomSpacing type="vertical" size="xs" />
                  <View style={styles.regenerateRow}>
                    <CustomTextInput
                      placeholder="e.g. Make the background brighter..."
                      value={reviewedRegenerateFeedback}
                      onChangeText={(text: string) => {
                        if (!reviewedImageKey) return;
                        setRegenerateFeedbackByImage((prev) => ({ ...prev, [reviewedImageKey]: text }));
                      }}
                      style={styles.regenerateInput}
                    />
                    <CustomSpacing type="horizontal" size="s" />
                    <CustomButtonCompat
                      text={isRegeneratingCurrentReviewedImage ? "Regenerating..." : "Regenerate"}
                      onPress={async () => {
                        if (!reviewedRegenerateFeedback.trim() || isRegeneratingCurrentReviewedImage) return;
                        if (!reviewedObjectiveId || !reviewedMeta?.text || !reviewedImageKey) return;
                        setRegeneratingReviewedImageKeys((prev) => ({ ...prev, [reviewedImageKey]: true }));
                        try {
                          await handleRegenerateSingle(
                            reviewedObjectiveId,
                            reviewedMeta.text,
                            reviewedImage.url,
                            reviewedRegenerateFeedback.trim()
                          );
                          setRegenerateFeedbackByImage((prev) => {
                            const next = { ...prev };
                            delete next[reviewedImageKey];
                            return next;
                          });
                        } finally {
                          setRegeneratingReviewedImageKeys((prev) => {
                            const next = { ...prev };
                            delete next[reviewedImageKey];
                            return next;
                          });
                        }
                      }}
                      disabled={
                        !reviewedRegenerateFeedback.trim() || isRegeneratingCurrentReviewedImage || !reviewedImage.id
                      }
                      styleType="primaryTransparentStroke"
                      size="s"
                      leftIcon={
                        isRegeneratingCurrentReviewedImage ? (
                          <ActivityIndicator size="small" color="#667085" />
                        ) : (
                          <Redo />
                        )
                      }
                      textStyle={{ color: "#667085", fontWeight: 700 }}
                      style={styles.regenerateButton}
                    />
                  </View>
                </>
              )}

              <View style={styles.imageReviewFooter}>
                <TouchableOpacity
                  style={styles.removeInlineButton}
                  onPress={() => {
                    if (!reviewedObjectiveId) return;
                    handleRemoveImage(reviewedObjectiveId, reviewedImage.url);
                    setImageReviewState(null);
                  }}>
                  <View style={styles.removeInlineButtonContent}>
                    <TrashIcon size={16} color="#F04438" />
                    <CustomText
                      text="Remove"
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                      style={{ color: "#F04438" }}
                    />
                  </View>
                </TouchableOpacity>

                <CustomButtonCompat
                  text={reviewedImage.approved ? "Approved" : "Approve"}
                  onPress={() => {
                    if (!reviewedObjectiveId) return;
                    setObjectiveImages((prev) => ({
                      ...prev,
                      [reviewedObjectiveId]: (prev[reviewedObjectiveId] || []).map((img) =>
                        img.url === reviewedImage.url ? { ...img, approved: true } : img
                      ),
                    }));
                  }}
                  disabled={reviewedImage.approved}
                  styleType="primary"
                  size="s"
                  leftIcon={<CheckMark width={16} height={16} fill="#FFFFFF" />}
                  textStyle={{ fontWeight: 700 }}
                  style={styles.approveButton}
                />
              </View>
            </ScrollView>
          </CustomModal>
        ) : null}
      </View>
    );
  };

  // Render step 5 content - Review
  const renderStep5Content = () => {
    const objectivesWithVideos = new Set();
    videoPool.forEach((video) => {
      if (video.objectiveSequence) {
        objectivesWithVideos.add(video.objectiveSequence);
      } else if (video.objectiveText) {
        const objective = objectives.findIndex(
          (obj) => obj.objective === video.objectiveText || obj.id === video.objectiveId
        );
        if (objective !== -1) {
          objectivesWithVideos.add(objective + 1);
        }
      }
    });

    return (
      <View>
        <CustomText text="Review your activity" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Review the details of your activity before creating it."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="m" />

        <View style={styles.reviewSection}>
          <CustomText text="Activity name" size="m" weight="ultraBold" useTranslationText={false} />
          <CustomText text={watchActivityName} size="m" useTranslationText={false} style={styles.reviewText} />
        </View>

        <CustomSpacing type="vertical" size="m" />

        <View style={styles.reviewSection}>
          <CustomText text="Activity Description" size="m" weight="ultraBold" useTranslationText={false} />
          <View style={styles.reviewTextContainer}>
            <CustomText
              text={watchDescription ? watchDescription.replace(/<[^>]*>/g, "") : ""}
              size="m"
              useTranslationText={false}
              style={styles.reviewText}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="m" />

        {/* Add Due Date Section */}
        <View style={styles.reviewSection}>
          <CustomText text="Due Date (Optional)" size="m" weight="ultraBold" useTranslationText={false} />
          <CustomSpacing type="vertical" size="s" />
          <Controller
            name="dueDate"
            control={control}
            rules={{ required: false }}
            render={({ field: { onChange, value } }) =>
              isFromLTI && Platform.OS === "web" ? (
                <input
                  type="date"
                  value={formatWebDateValue(value as Date | string | null | undefined)}
                  onChange={(event) => onChange(parseWebDateValue(event.target.value))}
                  style={WEB_DATE_INPUT_STYLE}
                  aria-label="Due Date date picker"
                />
              ) : (
                <DatePickerInput
                  locale="en"
                  value={value instanceof Date ? value : value ? new Date(value) : undefined}
                  onChange={onChange}
                  inputMode="start"
                  mode="outlined"
                  outlineColor={"#D1D5DB"}
                  selectionColor={"#3E68FE"}
                  activeOutlineColor={"blue"}
                  withDateFormatInLabel={false}
                  placeholder="MM/DD/YYYY"
                  theme={undefined}
                  style={styles.datePicker}
                  accessibilityLabel="Due Date date picker"
                />
              )
            }
          />
          {watchDueDate && (
            <CustomText
              text={`Due: ${new Date(watchDueDate).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}`}
              size="s"
              useTranslationText={false}
              style={[styles.reviewText, { marginTop: 8, color: "#3E68FE" }]}
            />
          )}
        </View>

        <CustomSpacing type="vertical" size="m" />

        <View style={styles.reviewSection}>
          <CustomText text="Learning Objectives" size="m" weight="ultraBold" useTranslationText={false} />
          {objectives.map((objective, index) => (
            <View key={index} style={styles.objectiveReviewItem}>
              <CustomText text={objective.objective} size="m" useTranslationText={false} style={styles.reviewText} />
              {objectivesWithVideos.has(index + 1) && (
                <CustomText text="Video" size="xs" useTranslationText={false} style={styles.videoTag} />
              )}
            </View>
          ))}
        </View>

        {hasGeneratedDemo && includeDemo && (
          <View style={styles.reviewSection}>
            <CustomText
              text="Interactive Demo Included"
              size="m"
              useTranslationText={false}
              style={styles.reviewText}
              weight="ultraBold"
            />
          </View>
        )}

        {watch("context") && watch("context").trim() !== "" && (
          <>
            <CustomSpacing type="vertical" size="m" />
            <View style={styles.reviewSection}>
              <CustomText text="Contextual Alignment" size="m" weight="ultraBold" useTranslationText={false} />
              <CustomText text={watch("context")} size="m" useTranslationText={false} style={styles.reviewText} />
            </View>
          </>
        )}

        <CustomSpacing type="vertical" size="m" />
        <View style={styles.activityState}>
          <CustomText text="Publish this Activity" size="l" weight="ultraBold" useTranslationText={false} />
          <CustomText
            text="Publish this activity now or schedule a publish date."
            size="s"
            useTranslationText={false}
            style={styles.stepDescription}
          />
          <Controller
            control={control}
            name="activityState"
            render={({ field: { onChange, value } }) => (
              <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
                {(Object.values(ActivityState) as ActivityState[]).map((state) => (
                  <CustomButtonCompat
                    key={state}
                    text={state}
                    size="s"
                    style={[styles.activityStateOption, state === value && styles.activeState]}
                    onPress={() => {
                      onChange(state);
                    }}
                  />
                ))}
              </div>
            )}
          />
          {watchActivityState === ActivityState.Scheduled && (
            <>
              <CustomText
                text="This Activity will automatically be published after:"
                size="s"
                useTranslationText={false}
                style={styles.stepDescription}
              />
              <Controller
                control={control}
                name="scheduledDate"
                render={({ field: { onChange, value } }) =>
                  isFromLTI && Platform.OS === "web" ? (
                    <div
                      style={{
                        width: "100%",
                        maxWidth: 250,
                        marginTop: 10,
                      }}>
                      <input
                        type="date"
                        value={formatWebDateValue(value as Date | string | null | undefined)}
                        onChange={(event) => onChange(parseWebDateValue(event.target.value))}
                        style={WEB_DATE_INPUT_STYLE}
                        aria-label="Schedule published date picker"
                      />
                    </div>
                  ) : (
                    <div style={{ maxWidth: 250, marginTop: 10 }}>
                      <DatePickerInput
                        locale="en"
                        value={value instanceof Date ? value : value ? new Date(value) : undefined}
                        onChange={onChange}
                        inputMode="start"
                        mode="outlined"
                        outlineColor={"#D1D5DB"}
                        selectionColor={undefined}
                        activeOutlineColor={"blue"}
                        withDateFormatInLabel={false}
                        placeholder="MM/DD/YYYY"
                        theme={{
                          colors: {
                            primary: "#000000",
                            onSurface: "#667085",
                          },
                          roundness: 8,
                        }}
                        style={styles.datePicker}
                        accessibilityLabel="Schedule published date picker"
                      />
                    </div>
                  )
                }
              />
            </>
          )}
        </View>
      </View>
    );
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1Content();
      case 2:
        return renderStep2Content();
      case 3:
        return renderStep3Content();
      case 4:
        return renderStep4Content();
      case 5:
        return renderStep5Content();
      default:
        return renderStep1Content();
    }
  };

  // Configure loading states for loading overlays
  const loadingConfig = {
    visible: generationState.isGenerating || evalGenerationState.isGenerating || generatingVisualization,
    step: generationState.isGenerating
      ? generationState.step
      : evalGenerationState.isGenerating
        ? evalGenerationState.step
        : "Generating interactive demo",
    progress: generationState.isGenerating
      ? generationState.progress
      : evalGenerationState.isGenerating
        ? evalGenerationState.progress
        : 1,
    message: generationState.isGenerating
      ? "Generating activity content..."
      : evalGenerationState.isGenerating
        ? "Generating evaluation criteria..."
        : "Creating an interactive visualization based on your content...",
    isCancellable: generationState.isGenerating
      ? generationState.isCancellable
      : evalGenerationState.isGenerating
        ? evalGenerationState.isCancellable
        : true,
    onCancel: generationState.isGenerating
      ? () => setGenerationState({ ...generationState, isGenerating: false })
      : evalGenerationState.isGenerating
        ? () => setEvalGenerationState({ ...evalGenerationState, isGenerating: false })
        : () => setGeneratingVisualization(false),
  };

  return (
    <View style={styles.container} id="interactive-learning-form-container">
      <View style={styles.formContainer}>
        <View style={styles.header}>
          <View style={styles.headerInner}>
            <CustomText
              text={`${activityData?.id ? "Edit" : "Create"} Interactive Learning Experience`}
              size="xl"
              weight="bold"
              useTranslationText={false}
            />
            <TouchableOpacity
              style={styles.headerCloseButton}
              onPress={onCancel}
              accessibilityRole="button"
              accessibilityLabel="Close interactive learning form">
              <CloseIcon fill="#667085" props={{ width: 20, height: 20 }} />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.stepsContainer}>{renderStepIndicator()}</View>

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
          tabIndex={0}>
          <View style={styles.content}>
            {renderStepContent()}

            <View style={styles.spacer} />

            {renderNavButtons()}
          </View>
        </ScrollView>

        {/* Loading Overlay */}
        {loadingConfig.visible && (
          <LoadingOverlay
            visible={loadingConfig.visible}
            step={loadingConfig.step}
            progress={loadingConfig.progress}
            message={loadingConfig.message}
            isCancellable={loadingConfig.isCancellable}
            onCancel={loadingConfig.onCancel}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  formContainer: {
    flex: 1,
    padding: 0,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 32,
  },
  stepIndicator: {
    marginBottom: 20,
  },
  stepProgressBar: {
    flexDirection: "row",
    marginTop: 10,
  },
  stepProgressSegment: {
    flex: 1,
    height: 4,
    marginHorizontal: 2,
    borderRadius: 2,
  },
  stepProgressActive: {
    backgroundColor: "#3E68FE",
  },
  stepProgressInactive: {
    backgroundColor: "#E6E6E6",
  },
  stepDescription: {
    color: "#667085",
    marginTop: 5,
  },
  inputWithButtonField: {
    marginBottom: 10,
  },
  dropdown: {
    borderColor: "#D1D5DB",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
  },
  editorContainer: {
    marginBottom: 20,
  },
  multilineInput: {
    height: 150,
    textAlignVertical: "top",
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  counter: {
    color: "#667085",
  },
  warningText: {
    color: "#DC2626",
  },
  reviewSection: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  reviewTextContainer: {
    marginTop: 10,
  },
  reviewText: {
    marginTop: 5,
  },
  objectiveReviewItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 10,
    padding: 5,
    backgroundColor: "#F2F4F7",
    borderRadius: 4,
  },
  videoTag: {
    backgroundColor: "#E6EBFF",
    color: "#3E68FE",
    padding: 4,
    borderRadius: 4,
  },
  spacer: {
    flex: 1,
  },
  navButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 24,
    marginBottom: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  navButton: {
    minWidth: 100,
  },
  errorText: {
    color: "#FF0000",
    marginTop: 8,
  },
  header: {
    backgroundColor: "#F2F4F7",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    padding: 0,
    width: "100%",
  },
  headerInner: {
    padding: 32,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  headerCloseButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
  },
  stepsContainer: {
    paddingHorizontal: 32,
    paddingVertical: 16,
  },
  infoBox: {
    backgroundColor: "#f5f7ff",
    borderRadius: 8,
    padding: 16,
  },
  bulletList: {
    marginVertical: 8,
    paddingLeft: 8,
  },
  datePicker: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D5D7DA",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  activityState: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  activeState: {
    backgroundColor: "#0084FF1A",
    color: "white",
    borderColor: "#0084FF",
  },
  activityStateOption: {
    borderRadius: 30,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderStyle: "solid",
    color: "#667085",
    borderColor: "transparent",
  },
  objectiveSection: {
    marginBottom: 16,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 0,
    borderWidth: 1,
    borderColor: "#EAECF0",
    overflow: "hidden",
  },
  objectiveTitleBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#F9FAFB",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  objectiveBody: {
    padding: 16,
  },
  objectiveActionsRow: {
    flexDirection: "row",
    gap: 12,
    paddingBottom: 16,
  },
  objectiveActionButton: {
    justifyContent: "center",
    paddingHorizontal: 14,
    borderColor: "#D0D5DD",
  },
  approveAllButton: {
    borderColor: "#66C61C",
    backgroundColor: "#ECFDF3",
  },
  emptyState: {
    paddingVertical: 40,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  emptyIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F2F4F7",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyActionsRow: {
    flexDirection: "row",
    gap: 12,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  gridItem: {
    width: "31%",
    minWidth: 200,
  },
  generateTile: {
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
    borderColor: "#D0D5DD",
    borderStyle: "dashed",
    gap: 4,
  },
  generateTileAction: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  imageReviewModal: {
    maxWidth: 1120,
    padding: 20,
  },
  imageReviewModalBody: {
    paddingTop: 12,
  },
  imageReviewScroll: {
    flex: 1,
  },
  imageReviewScrollContent: {
    flexGrow: 1,
    paddingBottom: 24,
  },
  imageReviewImageWrap: {
    width: "100%",
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "#F2F4F7",
    position: "relative",
    maxHeight: 560,
  },
  imageReviewImage: {
    width: "100%",
    height: "100%",
  },
  imageNavButton: {
    position: "absolute",
    top: "50%",
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#EAECF0",
    alignItems: "center",
    justifyContent: "center",
    transform: [{ translateY: -20 }],
  },
  imageNavLeft: {
    left: 16,
  },
  imageNavRight: {
    right: 16,
  },
  imageReviewTextInput: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 10,
  },
  regenerateRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  regenerateInput: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 10,
  },
  regenerateButton: {
    justifyContent: "center",
  },
  imageReviewFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 24,
  },
  removeInlineButton: {
    paddingVertical: 10,
    paddingHorizontal: 6,
  },
  removeInlineButtonContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  approveButton: {
    minWidth: 150,
    justifyContent: "center",
  },
});

export default InteractiveLearningFormCore;
